import React from "react";
import { formatMoney } from "../lib/constants";
import BuildingProfileNotice from "./BuildingProfileNotice";

/**
 * Renders a breakdown widget (per-symbol or per-broker) as a small bar list.
 * `items`: array of { label, count, total_pnl, win_rate }
 * Sorted upstream by abs(pnl) desc.
 */
export default function BreakdownWidget({
  title, label, items = [], currency, testid, onItemClick,
  lowSample = false, tradesRemaining = 0,
}) {
  const maxAbs = items.reduce((m, it) => Math.max(m, Math.abs(Number(it.total_pnl) || 0)), 0);

  return (
    <div
      data-testid={testid}
      className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-5 flex flex-col"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="bt-label">{label}</p>
          <h3 className="font-heading text-xl mt-1">{title}</h3>
        </div>
        <span className="text-xs text-[var(--bt-text-muted)] font-mono">
          {items.length} {items.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {lowSample ? (
        <BuildingProfileNotice
          tradesRemaining={tradesRemaining}
          testId={`${testid}-placeholder`}
        />
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--bt-text-muted)] py-6 text-center">
          No data yet — log trades to see breakdown.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.slice(0, 8).map((it) => {
            const pnl = Number(it.total_pnl) || 0;
            const positive = pnl >= 0;
            const width = maxAbs > 0 ? Math.max(4, (Math.abs(pnl) / maxAbs) * 100) : 4;
            const clickable = typeof onItemClick === "function";
            return (
              <li key={it.label} data-testid={`breakdown-row-${it.label}`}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={clickable ? () => onItemClick(it.label) : undefined}
                  data-testid={`breakdown-chip-${it.label}`}
                  className={`w-full text-left ${clickable ? "hover:bg-[var(--bt-surface-hover)] -mx-2 px-2 py-1 rounded-sm transition-colors duration-150 cursor-pointer" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1.5 text-sm">
                    <span className="font-semibold truncate pr-2 flex items-center gap-1.5">
                      {it.label}
                      {clickable && <span className="text-[var(--bt-accent)] text-xs opacity-0 group-hover:opacity-100">→</span>}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[var(--bt-text-muted)] text-xs">
                        {it.count} · {(it.win_rate ?? 0).toFixed(2)}%
                      </span>
                      <span className={`bt-num font-bold ${positive ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}`}>
                        {formatMoney(pnl, currency)}
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-[var(--bt-surface-hover)] rounded-sm overflow-hidden">
                    <div
                      className={`h-full ${positive ? "bg-[var(--bt-accent)]" : "bg-[var(--bt-danger)]"}`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
